import { useCallback, useRef, useState } from "react";

import { Cluster, usePingClusterMutation } from "../store/pomeriumApi";

export type PingClusterTrigger = (
  organizationId: string,
  cluster: Cluster,
) => Promise<void>;
export type PingClusterResult = {
  loading: boolean;
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
};

type PingClusterData = {
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
};

const abortedPingClusterData: PingClusterData = {
  success: false,
  errorCode: "aborted",
  errorMessage: "aborted",
};

// usePingCluster is a hook that returns a trigger to ping a cluster.
// it uses the API usePingClusterMutation and falls back to browser-based
// fetch.
function usePingCluster(): [PingClusterTrigger, PingClusterResult] {
  const abortController = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PingClusterData>({ success: false });
  const [triggerPingCluster] = usePingClusterMutation();

  // cloud ping uses the API to initiate a ping from the cloud
  const cloudPing = async (
    abortSignal: AbortSignal,
    organizationId: string,
    cluster: Cluster,
  ): Promise<PingClusterData> => {
    if (!organizationId || !cluster?.id) {
      return { success: false };
    }

    if (abortSignal.aborted) {
      return abortedPingClusterData;
    }

    // start the ping
    const mutation = triggerPingCluster({
      organizationId,
      clusterId: cluster?.id,
    });

    return await new Promise<PingClusterData>((resolve) => {
      // on abort we want to abort the ping
      const abortHandler = () => {
        mutation.abort();
        resolve(abortedPingClusterData);
      };
      abortSignal.addEventListener("abort", abortHandler);

      (async () => {
        try {
          const res = await mutation;
          if ("error" in res) {
            resolve({
              success: false,
              errorCode: "unknown",
              errorMessage: `${res.error}`,
            });
          } else if ("data" in res) {
            resolve(res.data);
          } else {
            resolve({
              success: false,
              errorCode: "unknown",
              errorMessage:
                "ping result had neither data nor an error which should never happen",
            });
          }
        } catch (e) {
          resolve({ success: false, errorCode: "error", errorMessage: `${e}` });
        } finally {
          abortSignal.removeEventListener("abort", abortHandler);
        }
      })();
    });
  };

  // browser ping makes a fetch call to the cluster
  const browserPing = async (
    abortSignal: AbortSignal,
    cluster: Cluster,
  ): Promise<PingClusterData> => {
    if (!cluster?.fqdn) {
      return { success: false };
    }

    if (abortSignal.aborted) {
      return abortedPingClusterData;
    }

    const url = `https://ping.${cluster.fqdn}/.well-known/pomerium/jwks.json`;
    try {
      const res = await fetch(url, {
        signal: abortSignal,
      });
      await res.json();
      return { success: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : `${e}`;
      return {
        success: false,
        errorCode: "unknown",
        errorMessage: `Get ${url}: ${msg}`,
      };
    }
  };

  const ping = async (
    abortSignal: AbortSignal,
    organizationId: string,
    cluster: Cluster,
  ): Promise<PingClusterData> => {
    const cloudPingResult = await cloudPing(
      abortSignal,
      organizationId,
      cluster,
    );
    if (cloudPingResult.success) {
      return cloudPingResult;
    }

    const browserPingResult = await browserPing(abortSignal, cluster);
    if (browserPingResult.success) {
      return browserPingResult;
    }

    return cloudPingResult;
  };

  const trigger = useCallback(
    async (organizationId: string, cluster: Cluster): Promise<void> => {
      abortController.current?.abort();

      abortController.current = new AbortController();
      setLoading(true);
      const data = await ping(
        abortController.current.signal,
        organizationId,
        cluster,
      );
      if (!abortController.current.signal.aborted) {
        setData(data);
        setLoading(false);
      }
      return;
    },
    [],
  );

  return [trigger, { loading, ...data }];
}

export default usePingCluster;
